import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { createSearchKey, FUNCTION_RESULT_STATUS, ICommonSearchKey, IFieldUpdate, returnJSONStringFromObject } from '@nova/common-interfaces';
import { IServicePlan, ISubscription } from '@rfm/shared-interfaces';
import {
  AccordionModule,
  AccordionTabCloseEvent,
  AccordionTabOpenEvent,
} from 'primeng/accordion';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ServicePlanService } from '../../../../shared/services/products/service-plan.service.mjs';
import { SubscriptionService } from '../../../../shared/services/products/subscription.service.mjs';

@Component({
  selector: 'app-subscription-change-plan-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    AccordionModule,
    BlockUIModule,
    ConfirmDialogModule,
    ToastModule,
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './subscription-change-plan.modal.html',
  styleUrls: ['./subscription-change-plan.modal.scss'],
})
export class SubscriptionChangePlanModal implements OnInit {
  @Input()
  objectType = signal('');
  @Input()
  objectId = signal('');
  @Input()
  visible = signal(false);
  subscription: ISubscription | undefined;
  plans: IServicePlan[] = [];
  currentPlanSlug: string = '';
  selectedPlanSlug: string = '';
  currentPlanName: string | undefined;
  customerName: string = "";
  isLoading = false;

  @Output() onData = new EventEmitter<any>();

  sendDataToParent() {
    this.onData.emit(true);
  }

  constructor(
    private servicePlanService: ServicePlanService,
    private subscriptionService: SubscriptionService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  ngOnInit() {}

  changePlan() {
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: 'Are you sure that you want to change this plan?',
      header: 'Change Plan Confirmation',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Change Plan',
      },
      accept: async () => {
        this.isLoading = true;
        const plan = this.plans.find(x => x.slug === this.selectedPlanSlug);

        if (!plan)
        {
          this.messageService.add({
            severity: 'error',
            summary: 'Subscription Plan Not Changed',
            detail: 'Customer subscription plan has not been changed',
            life: 3000,
          });
          this.isLoading = false;
          return Promise.reject();
        }

        const fieldsToUpdate: ICommonSearchKey[] = [];
        
        fieldsToUpdate.push(createSearchKey("plan_id", plan?.id));
        fieldsToUpdate.push(createSearchKey("plan", returnJSONStringFromObject<IServicePlan>(plan)));
        fieldsToUpdate.push(createSearchKey("amount", plan?.amount));
        fieldsToUpdate.push(createSearchKey("actual_amount", plan?.amount));

        //*************************************
        //TODO: add workflow to credit and debit customer account for plan change
        // */

        await this.subscriptionService.updateByField({
          "key": this.subscription!.id,
          "updateValues": fieldsToUpdate
        } as IFieldUpdate).then(result => {
          if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS)
          {
            this.messageService.add({
              severity: 'error',
              summary: 'Subscription Plan Not Changed',
              detail: 'Customer subscription plan has not been changed',
              life: 3000,
            });
            this.isLoading = false;
            return Promise.reject(result);
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Subscription Plan Changed',
            detail: 'Customer subscription plan has been changed',
            life: 3000,
          });
          this.isLoading = false;
          this.sendDataToParent();
          this.visible.set(false);
          return Promise.resolve();
        })
        .catch(err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Subscription Plan Not Changed',
            detail: 'Customer subscription plan has not been changed',
            life: 3000,
          });
          this.isLoading = false;
          return Promise.reject(err);
        })
      },
      reject: () => {
        this.isLoading = false;
        this.visible.set(false);
      },
    });
  }

  panelOpened(event: AccordionTabOpenEvent) {
    console.log(event);
    this.selectedPlanSlug = event.index.toString();
  }

  panelClosed(event: AccordionTabCloseEvent) {
    console.log(event);
    if ((this.selectedPlanSlug = event.index.toString())) {
      this.selectedPlanSlug = '';
    }
  }

  dialogClosed() {
    console.log('Closed');
    this.plans = [];
    this.currentPlanSlug = '';
    this.selectedPlanSlug = '';
    this.currentPlanName = '';
    this.customerName = "";
  }

  dialogShown() {
    this.isLoading = true;
    console.log(this.objectId());
    if (this.objectType() === 'customer') {
      this.subscriptionService
        .getByCustomer(this.objectId())
        .then(async (result) => {
          if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
            this.subscription = result.data![0];
            this.customerName = result.data![0].customer_id?.name!;
            this.currentPlanSlug = result.data![0].plan?.slug ?? '';
            this.currentPlanName = result.data![0].name;
          }
          await this.servicePlanService.getAll().then((result) => {
            if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
              this.plans = result.data!;
            }
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
    if (this.objectType() === 'subscription') {
      this.subscriptionService
        .get(this.objectId())
        .then(async (result) => {
          if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
            this.subscription = result.data!;
            this.customerName = result.data!.customer_id?.name!;
            this.currentPlanSlug = result.data!.plan?.slug ?? '';
            this.selectedPlanSlug = result.data!.plan?.slug ?? '';
            this.currentPlanName = result.data!.name;
          }
          await this.servicePlanService.getAll().then((result) => {
            if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
              this.plans = result.data!;
            }
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
}
